/**
 * workspaceApi.ts
 * Feature: 116 - Entity Detail Modals
 * Author: Peter Dobler
 * Date: 2026-01-29
 * Constitution Version: 1.12.0
 *
 * Purpose: Frontend API service for workspace entities.
 */

import { apiClient } from './apiClient';

/** Workspace entity */
export interface Workspace {
  id: string;
  fabric_workspace_id: string;
  workspace_name: string;
  display_name?: string;
  capacity_id: string;
  client_account_id?: string;
  status?: string;
  description?: string;
  type?: string;
  state?: string;
  // Control Plane metadata
  owner_id?: string;
  backup_priority?: number;
  // NOTE: cost_center / notes were removed — dbo.workspaces has no columns for
  // them, so the form fields silently discarded input. Add a migration first if
  // these are ever wanted.
  tags?: string[];
  // Computed fields
  owner_name?: string;
  capacity_name?: string;
  client_name?: string;
  /** Spec 298 FR-002: the client the open assignment names when that client is retired (client_name is then empty) */
  retired_client_name?: string | null;
  retired_client_at?: string | null;
  lakehouse_count?: number;
  last_activity_at?: string;
  created_at: string;
  updated_at: string;
}

/** Workspace update request (control-plane fields with real dbo columns only) */
export interface UpdateWorkspaceRequest {
  display_name?: string;
  owner_id?: string;
  backup_priority?: number;
  tags?: string[];
}

/** Minimal user shape for the Owner dropdown (from GET /api/users). */
export interface WorkspaceOwnerOption {
  id: string;
  display_name?: string;
  email?: string;
}

/** Workspace filters */
export interface WorkspaceFilters {
  capacity_id?: string;
  client_account_id?: string;
  status?: string;
  state?: string;
}

/**
 * Get workspace by ID
 */
export async function getWorkspaceById(id: string): Promise<Workspace> {
  const response = await apiClient.get<{ data: Workspace }>(`/api/v1/workspaces/${id}`);
  return response.data.data;
}

/**
 * Get all workspaces with optional filters
 */
export async function getWorkspaces(filters?: WorkspaceFilters): Promise<Workspace[]> {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, String(value));
      }
    });
  }
  const queryString = params.toString();
  const url = queryString ? `/api/v1/workspaces?${queryString}` : '/api/v1/workspaces';
  const response = await apiClient.get<{ data: Workspace[] }>(url);
  return response.data.data;
}

/**
 * Get workspaces by capacity
 */
export async function getWorkspacesByCapacity(capacityId: string): Promise<Workspace[]> {
  const response = await apiClient.get<{ data: Workspace[] }>(
    `/api/v1/workspaces?capacity_id=${capacityId}`
  );
  return response.data.data;
}

/**
 * Get assignable users for the Owner dropdown.
 * GET /api/users returns { success, data: User[], pagination }; each user
 * exposes id + display_name/email.
 */
export async function getWorkspaceOwners(): Promise<WorkspaceOwnerOption[]> {
  const response = await apiClient.get<{ data: WorkspaceOwnerOption[] }>('/api/users');
  return response.data.data ?? [];
}

/**
 * Update workspace (Control Plane metadata only)
 */
export async function updateWorkspace(
  id: string,
  data: UpdateWorkspaceRequest
): Promise<Workspace> {
  const response = await apiClient.patch<{ data: Workspace }>(`/api/v1/workspaces/${id}`, data);
  return response.data.data;
}

// TanStack Query hooks

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

/** Query key for workspaces */
export const WORKSPACE_QUERY_KEYS = {
  all: ['workspaces'] as const,
  lists: () => [...WORKSPACE_QUERY_KEYS.all, 'list'] as const,
  list: (filters?: WorkspaceFilters) => [...WORKSPACE_QUERY_KEYS.lists(), filters] as const,
  details: () => [...WORKSPACE_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...WORKSPACE_QUERY_KEYS.details(), id] as const,
  byCapacity: (capacityId: string) => [...WORKSPACE_QUERY_KEYS.all, 'by-capacity', capacityId] as const,
};

/**
 * Hook to fetch workspace by ID
 */
export function useWorkspace(
  id: string | null,
  options?: Omit<UseQueryOptions<Workspace>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: WORKSPACE_QUERY_KEYS.detail(id ?? ''),
    queryFn: () => getWorkspaceById(id!),
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook to fetch all workspaces
 */
export function useWorkspaces(
  filters?: WorkspaceFilters,
  options?: Omit<UseQueryOptions<Workspace[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: WORKSPACE_QUERY_KEYS.list(filters),
    queryFn: () => getWorkspaces(filters),
    ...options,
  });
}

/**
 * Hook to fetch workspaces by capacity
 */
export function useWorkspacesByCapacity(
  capacityId: string | null,
  options?: Omit<UseQueryOptions<Workspace[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: WORKSPACE_QUERY_KEYS.byCapacity(capacityId ?? ''),
    queryFn: () => getWorkspacesByCapacity(capacityId!),
    enabled: !!capacityId,
    ...options,
  });
}

/**
 * Hook to fetch assignable users for the Owner dropdown.
 */
export function useWorkspaceOwners(
  options?: Omit<UseQueryOptions<WorkspaceOwnerOption[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...WORKSPACE_QUERY_KEYS.all, 'owners'] as const,
    queryFn: getWorkspaceOwners,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to update workspace
 */
export function useUpdateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkspaceRequest }) =>
      updateWorkspace(id, data),
    onSuccess: (updatedWorkspace) => {
      // Invalidate and update cache
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.all });
      queryClient.setQueryData(
        WORKSPACE_QUERY_KEYS.detail(updatedWorkspace.id),
        updatedWorkspace
      );
    },
  });
}

export default {
  getWorkspaceById,
  getWorkspaces,
  getWorkspacesByCapacity,
  updateWorkspace,
};
